import { useCallback, useEffect, useState } from 'react';

export type HistoryItem = {
  comicId: string;
  chapterId: string;
  chapterTitle: string;
  viewedAt: number;
};

const STORAGE_KEY = 'douman_history';
const MAX_ITEMS = 40;
const EVENT = 'douman-history-change';

function readHistory(): HistoryItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeHistory(items: HistoryItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new Event(EVENT));
  } catch {
    /* ignore storage errors */
  }
}

export function useHistory() {
  const [history, setHistory] = useState<HistoryItem[]>([]);

  useEffect(() => {
    setHistory(readHistory());
    const handler = () => setHistory(readHistory());
    window.addEventListener(EVENT, handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const addHistory = useCallback((entry: Omit<HistoryItem, 'viewedAt'>) => {
    const current = readHistory().filter((item) => item.comicId !== entry.comicId);
    const next = [{ ...entry, viewedAt: Date.now() }, ...current].slice(0, MAX_ITEMS);
    writeHistory(next);
  }, []);

  const removeHistory = useCallback((comicId: string) => {
    const next = readHistory().filter((item) => item.comicId !== comicId);
    writeHistory(next);
  }, []);

  const clearHistory = useCallback(() => {
    writeHistory([]);
  }, []);

  return { history, addHistory, removeHistory, clearHistory };
}